import { useEffect, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { isUndefined } from 'lodash';

import PLTable from 'components/PLTable';
import { CookieNames, ElementNames } from 'constants/app';
import { DEFAULT_MARKET_UNITS } from 'constants/placement';
import { getMobileSwipeMenu } from 'redux/modules/appConfigs/selectors';
import { getElementHeightByName } from 'redux/modules/appSettings/selectors';
import { getLoggedInStatusState } from 'redux/modules/auth/selectors';
import { getMarketShowAll } from 'redux/modules/market/selectors';
import { getIsMarketClosed, getMarketPricesCurrency } from 'redux/modules/marketsPrices/selectors';
import { IMarket } from 'types/markets';
import { getMarketTypes } from 'utils/market';

import { isShowPLTablePopup } from '../../helpers';
import MarketActionsRow from '../MarketActionsRow';

import styles from './MobileSelectionActionsAndPLTable.module.scss';

type MobileSelectionActionsAndPLTableProps = {
  market: IMarket;
  onShowAllRunners: () => void;
  onMinimize: () => void;
  isShowPlTable: boolean;
  onTogglePLTable: () => void;
  isOpeningSoonStatus: boolean;
  showAllRunners?: boolean;
};

export default function MobileSelectionActionsAndPLTable({
  market,
  onShowAllRunners,
  isShowPlTable,
  onMinimize,
  onTogglePLTable,
  isOpeningSoonStatus,
  showAllRunners
}: MobileSelectionActionsAndPLTableProps) {
  const { t } = useTranslation();
  const [cookies] = useCookies([CookieNames.PL_TABLE_VISIBLE]);
  const { isHandicapDoubleLine } = getMarketTypes(market.description.marketType, market.description.bettingType);

  const marketShowAll = useSelector(getMarketShowAll);
  const marketPricesCurrency = useSelector(getMarketPricesCurrency(market.marketId));
  const swipeMenu = useSelector(getMobileSwipeMenu);
  const swipeMenuHeight = useSelector(getElementHeightByName(ElementNames.MOBILE_SWIPE_MENU_HEIGHT));
  const isClosed = useSelector(getIsMarketClosed(market.marketId));
  const isLoggedIn = useSelector(getLoggedInStatusState);
  const showAll = !isUndefined(showAllRunners) ? showAllRunners : marketShowAll;

  const [isStuck, setIsStuck] = useState(false);

  const contentRef = useRef<HTMLDivElement>(null);

  const isMarketClosedInitially = isClosed && !!market?.closedDate;
  const isShowPlTablePopup = isShowPLTablePopup(market.description.bettingType, market.description.marketType);

  useEffect(() => {
    if (showAll) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsStuck(!entry.isIntersecting);
        },
        {
          threshold: 1,
          rootMargin: '-1px 0px 0px 0px' // triggers just before it sticks
        }
      );

      if (contentRef.current) {
        observer.observe(contentRef.current);
      }

      return () => {
        observer.disconnect();
      };
    }
  }, [showAll]);

  return (
    <>
      <div ref={contentRef} className={styles.stuckDetector} style={swipeMenu ? { top: 80 } : undefined} />
      <div
        className={classNames(styles.wrapper, {
          [styles.stickyWrapper]: showAll,
          [styles.stickyWrapper__shadow]: showAll && isStuck
        })}
        style={swipeMenu ? { top: swipeMenuHeight - 1 } : undefined}
      >
        {isMarketClosedInitially && (
          <p className={classNames('biab_closed-market-status', styles.closedLabel)}>
            {t('market.selections.status.closed')}
          </p>
        )}
        <MarketActionsRow
          market={market}
          showAll={showAll}
          onShowAllRunners={onShowAllRunners}
          onMinimize={onMinimize}
          isShowPlTable={isShowPlTable}
          onTogglePLTable={onTogglePLTable}
          isOpeningSoonStatus={isOpeningSoonStatus}
          ignoreActions={!isHandicapDoubleLine}
          isStandaloneWidget
        />
        {cookies.PL_TABLE_VISIBLE && isShowPlTable && isLoggedIn && (
          <div
            className={classNames(styles.mobilePLTableWrapper, {
              [styles.mobilePLTableWrapper__popup]: isShowPlTablePopup
            })}
          >
            <PLTable
              currencyCode={marketPricesCurrency}
              marketId={market.marketId}
              bettingType={market.description.bettingType}
              marketType={market.description.marketType}
              units={market?.description?.lineRangeInfo?.marketUnit || DEFAULT_MARKET_UNITS}
            />
          </div>
        )}
      </div>
    </>
  );
}
